import { Box, Stack, TextField, Typography } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';

import { useGetOwnIntegratorFeaturesQuery } from '@/api/v1';
import { CustomSelect } from '@/components/molecules/CustomSelect';
import { FileInput } from '@/components/molecules/FileInput';
import { Section } from '@/components/molecules/Section';
import { Simulation } from '@/hooks/useSimulation';
import { useTranslator } from '@/hooks/useTranslator';
import {
  purposeOfTransferOptions,
  PurposeOfTransferValue,
} from '@/Screens/Dashboard/Flows/TransferFlow/data';
import { indiaPurposeOfTransferOptions } from '@/Screens/Dashboard/Flows/TransferFlow/india-pop-codes';
import { CountryType } from '@/types/country';
import { CurrencyCodeType } from '@/types/currency';
import { SelectItemWithDescription } from '@/types/fields';

const CHINESE_CURRENCIES: readonly CurrencyCodeType[] = [
  CurrencyCodeType.HKD,
  CurrencyCodeType.CNY,
];

interface Props {
  readonly error: string | null;
  readonly documentRequired: boolean;
  readonly creatingTransaction: boolean;
  readonly simulation: Simulation;
  readonly attachment: File | null;
  readonly transferPurpose: string;
  readonly paymentReference: string;
  readonly paymentReferenceError?: string | null;
  readonly countryCode: CountryType;
  readonly currency: CurrencyCodeType;

  onFileChange(event: React.ChangeEvent<HTMLInputElement>): void;
  onFileRemove(): void;
  onPurposeChange(event: React.ChangeEvent<HTMLInputElement>): void;
  onPaymentReferenceChange(event: React.ChangeEvent<HTMLInputElement>): void;
}

export const DocumentSections: React.FC<Props> = (props: Props): React.ReactElement => {
  const {
    error,
    countryCode,
    currency,
    documentRequired,
    simulation,
    creatingTransaction,
    attachment,
    transferPurpose,
    paymentReference,
    onFileChange,
    onFileRemove,
    onPurposeChange,
    onPaymentReferenceChange,
    paymentReferenceError,
  } = props;
  const tr = useTranslator();
  const { data: integratorFeatures } = useGetOwnIntegratorFeaturesQuery();

  const countrySpecificPurposeOfTransferOptions =
    React.useMemo((): ReadonlyArray<SelectItemWithDescription> => {
      if (countryCode === CountryType.IN) {
        return indiaPurposeOfTransferOptions;
      } else if (CHINESE_CURRENCIES.includes(currency)) {
        return purposeOfTransferOptions.filter(
          (option: SelectItemWithDescription<PurposeOfTransferValue>): boolean =>
            option.value === 'invoiceAndSupplierPayments',
        );
      } else {
        const filteredPurposeOfTransferOptions = purposeOfTransferOptions.filter(
          (transferPurpose: SelectItemWithDescription<PurposeOfTransferValue>): boolean => {
            if (transferPurpose.value !== 'eFxOperation') {
              return true;
            }

            return integratorFeatures?.efxEnabled === true;
          },
        );

        return filteredPurposeOfTransferOptions;
      }
    }, [countryCode, currency, integratorFeatures?.efxEnabled]);

  if (!documentRequired) {
    return (
      <Section
        title={tr('INTERNAL_NOTES_OPTIONAL')}
        description={tr('INTERNAL_NOTES_DESCRIPTION')}
        working={creatingTransaction}
        visible={!!simulation.id}>
        <Stack gap={4}>
          <Box sx={styles.attachment}>
            <Typography variant="h7">
              <Typography variant="inherit" component="span" color="text.primary">
                {tr('ADD_DOCUMENTS')}
              </Typography>
              <Typography variant="inherit" component="span">
                {' '}
              </Typography>
              <Typography variant="inherit" component="span" color="custom.LightGray">
                {tr('INVOICES_RECEIPTS_ETC')}
              </Typography>
            </Typography>
            <FileInput
              name="attachments"
              value={attachment}
              status={QueryStatus.uninitialized}
              error={null}
              onChange={onFileChange}
              onRemove={onFileRemove}
            />
          </Box>
          <TextField
            label={tr('PAYMENT_REFERENCE', allowedLength)}
            multiline={true}
            rows={3}
            value={paymentReference}
            fullWidth={true}
            slotProps={paymentReferenceSlotProps}
            onChange={onPaymentReferenceChange}
            error={!!paymentReferenceError}
            helperText={paymentReferenceError}
          />
        </Stack>
      </Section>
    );
  }

  return (
    <>
      <Section
        title={tr('SUPPORTING_DOCUMENTATION')}
        description={tr('SUPPORTING_DOCUMENTATION_DESCRIPTION')}
        working={creatingTransaction}
        visible={!!simulation.id}>
        <Stack gap={4}>
          <CustomSelect
            label={tr('SELECT_PURPOSE_FOR_TRANSFER')}
            fullWidth={true}
            items={countrySpecificPurposeOfTransferOptions}
            value={transferPurpose}
            onChange={onPurposeChange}
          />
          {transferPurpose && transferPurpose !== 'eFxOperation' && (
            <Box sx={styles.attachment}>
              <Typography variant="h7">
                <Typography variant="inherit" component="span" color="text.primary">
                  {tr('ADD_DOCUMENTS')}
                </Typography>
                <Typography variant="inherit" component="span">
                  {' '}
                </Typography>
                <Typography variant="inherit" component="span" color="custom.LightGray">
                  {tr('INVOICES_RECEIPTS_ETC')}
                </Typography>
              </Typography>
              <Stack gap={2}>
                <FileInput
                  name="attachments"
                  value={attachment}
                  status={QueryStatus.uninitialized}
                  error={null}
                  onChange={onFileChange}
                  onRemove={onFileRemove}
                />
                {error && (
                  <Box textAlign="center">
                    <Typography color="error">{error}</Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </Stack>
      </Section>

      <Section
        title={tr('RECIPIENT_MEMO_OPTIONAL')}
        description={tr('PAYMENT_REFERENCE_DESCRIPTION')}
        working={creatingTransaction}
        visible={!!simulation.id}>
        <TextField
          label={tr('PAYMENT_REFERENCE', allowedLength)}
          multiline={true}
          rows={3}
          fullWidth={true}
          value={paymentReference}
          slotProps={paymentReferenceSlotProps}
          onChange={onPaymentReferenceChange}
          error={!!paymentReferenceError}
          helperText={paymentReferenceError}
        />
      </Section>
    </>
  );
};

const allowedLength = 100;
const paymentReferenceSlotProps = { htmlInput: { maxLength: allowedLength } };

const styles: Record<string, SystemStyleObject> = {
  attachment: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'custom.DividerLight',
    borderRadius: 1,
    p: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
};
